<template>
  <div class="table-node__container" :id="`table-${value.uuid}`">
    <div class="table-node__header">
      <span
        class="table-node__title"
        :id="`caption-${value.uuid}`"
        v-if="value.meta && value.meta.caption"
      >
        {{ value.meta.caption }}
      </span>
      <div class="table-node__options">
        <slot name="table-options" :isVisible="isVisible" :toggleIsVisible="toggleIsVisible">
          <button class="table-node__options-btn" @click="toggleIsVisible">
            {{ isVisibleBtnText }}
          </button>
        </slot>
      </div>
    </div>

    <table class="table-node" :aria-labelledby="`caption-${value.uuid}`" v-show="isVisible">
      <thead class="table-node__header-row">
        <tr>
          <th
            scope="col"
            v-for="(columnName, index) in data.columnNames"
            :key="`th-${index}`"
            class="table-node__heading-cell overflow-hover"
            :title="columnName"
          >
            {{ columnName }}
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(row, idx) in data.data">
          <tr
            v-if="row && row.filter((el) => Array.isArray(el)).length === 0"
            :key="idx"
            class="table-node__row"
          >
            <th scope="row" class="table-node__heading-cell" :title="data.rowNames[idx]">
              {{ data.rowNames[idx] }}
            </th>
            <Cell
              v-for="(cellData, cellIdx) in row"
              :key="cellIdx"
              :data="cellData"
              :uuid="value.uuid"
              class="table-node__cell"
            />
          </tr>
          <tr
            v-else
            v-for="(groupedRow, groupIdx) in row"
            :key="'group-' + groupIdx"
            class="table-node__row"
          >
            <th
              v-if="groupIdx === 0"
              :rowspan="row.length"
              scope="row"
              class="table-node__heading-cell"
              :title="data.rowNames[idx]"
            >
              {{ data.rowNames[idx] }}
            </th>
            <Cell
              v-for="(cellData, cellIdx) in groupedRow"
              :key="cellIdx"
              :data="cellData"
              :uuid="value.uuid"
              class="table-node__cell"
            />
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script>
import PigeonDocNodeModel from '../../models/PigeonDocNodeModel';
import Cell from './Cell';
import addOverflowHover from '../../lib/addOverflowHover';

const TRANSLATIONS = {
  show: {
    en: 'show table',
    fr: 'afficher le tableau',
  },
  hide: {
    en: 'hide table',
    fr: 'masquer le tableau',
  },
};

export default {
  name: 'DisplayTable',
  components: { Cell },
  props: {
    value: PigeonDocNodeModel,
  },
  mounted() {
    addOverflowHover(this.$el);
  },
  computed: {
    data() {
      return this.value.content.data;
    },
    isVisible() {
      // state is not defined or its set to "visible" or "hidden"
      return !this.value.meta || !this.value.meta.state || this.value.meta.state === 'visible';
    },
    isVisibleBtnText() {
      let lang = this.value.meta.lang || 'en';
      return TRANSLATIONS[this.isVisible ? 'hide' : 'show'][lang];
    },
  },
  methods: {
    toggleIsVisible() {
      const state = this.isVisible ? 'hidden' : 'visible';

      // clone the node to avoid mutating the prop
      const updated = {
        ...this.value,
        meta: {
          ...this.value.meta,
          state,
        },
      };

      this.$emit('updateNode', updated);
    },
  },
};
</script>

<style>
.table-node {
  border-collapse: collapse;
  max-width: 100ch;
  overflow: auto;
  display: block;
}

.table-node__container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 4rem 0;
}

.table-node__header {
  display: flex;
  align-items: center;
  flex: 100%;
  padding: 0.5rem;
}

.table-node__title {
  display: block;
  font-weight: bold;
}

.table-node__options {
  margin-left: auto;
}

.table-node__heading-cell {
  padding: 0.5rem;
  border: 1px solid #959595;
}

.table-node__heading-cell:first-child {
  border-left: none;
}

.table-node__heading-cell:last-child {
  border-right: none;
}

.table-node__header-row .table-node__heading-cell {
  border-top: none;
}

.table-node__row:last-child .table-node__heading-cell {
  border-bottom: none;
}

.table-node__cell {
  padding: 0.5rem;
  border: 1px solid #959595;
}

.table-node__cell:last-child {
  border-right: none;
}

.table-node__row:last-child .table-node__cell {
  border-bottom: none;
}
</style>
